import { storeToRefs } from "pinia";
import { useTasksStore, useUsersStore } from "@/stores/v2";
import { reactive } from "vue";
import { v4 as uuidv4 } from "uuid";
import { TaskHistoryEntry } from "@/types/v2/task/vaccination/history.types";

let instance: ReturnType<typeof createTaskHistory> | null = null;

/**
 * Internal function for the actual task history implementation
 */
function createTaskHistory() {
  // ===================================================================
  // Store
  // ===================================================================
  const { activeTask } = storeToRefs(useTasksStore());
  const activeUser = useUsersStore().getCurrentUser;

  // ===================================================================
  // State
  // ===================================================================
  // Justification Modal
  const justificationModal = reactive({
    isOpen: false,
    fieldPath: '',
    fieldName: '',
    justification: '',
    oldValue: null as any,
    newValue: null as any,
    callback: null as ((justification: string) => void) | null,
    rejectCallback: null as ((reason?: any) => void) | null
  });

  // ===================================================================
  // Getters
  // ===================================================================
  // Get Task Changes History
  const getHistory = (): TaskHistoryEntry[] | [] => {
    if (!activeTask.value || !activeTask.value.history) return [];
    return activeTask.value.history.entries;
  };

  // ===================================================================
  // Setters
  // ===================================================================
  // Record Task Changes to History
  const storeChange = (record: TaskHistoryEntry): boolean => {
    if (!activeUser || !activeTask.value) return false;

    if (!activeTask.value.history) {
      activeTask.value.history = { entries: [] }
    }

    activeTask.value.history.entries.push(record);

    return true;
  };

  // ===================================================================
  // Actions
  // ===================================================================
  // Require Justification modal
  const requireJustification = (params: {
    fieldPath: string;
    fieldName: string;
    oldValue: any;
    newValue: any;
    justification: string;
  }): Promise<string> => {
    return new Promise((resolve, reject) => {
      justificationModal.fieldPath = params.fieldPath;
      justificationModal.fieldName = params.fieldName;
      justificationModal.oldValue = params.oldValue;
      justificationModal.newValue = params.newValue;
      justificationModal.justification = params.justification || '';
      justificationModal.callback = resolve;
      justificationModal.rejectCallback = reject;
      justificationModal.isOpen = true;
    });
  };

  // Submit justification
  const submitJustification = (): void => {
    if (justificationModal.callback) {
      justificationModal.callback(justificationModal.justification);
    }
    closeJustificationModal();
  };

  // Cancel justification creation
  const cancelJustification = (): void => {
    if (justificationModal.rejectCallback) {
      justificationModal.rejectCallback(new Error('Justification cancelled'));
    } else if (justificationModal.callback) {
      // Fallback to empty string if no reject callback
      justificationModal.callback('');
    }
    closeJustificationModal();
  };

  // Close Justification Modal
  const closeJustificationModal = (): void => {
    justificationModal.fieldName = '';
    justificationModal.fieldPath = '';
    justificationModal.oldValue = null;
    justificationModal.newValue = null;
    justificationModal.callback = null;
    justificationModal.rejectCallback = null;
    justificationModal.justification = '';
    justificationModal.isOpen = false;
  };

  // Record Change
  const recordChange = async (params: {
    fieldPath: string;
    fieldName: string;
    oldValue: any;
    newValue: any;
    context: string;
    contextType: string;
    workflowStage: string;
    requireJustification?: boolean;
    justification?: string;
  }): Promise<boolean> => {
    try {
      if (!activeUser) return false;

      let justification = params.justification || '';

      if (params.requireJustification !== false) {
        justification = await requireJustification({
          fieldPath: params.fieldPath,
          fieldName: params.fieldName,
          oldValue: params.oldValue,
          newValue: params.newValue,
          justification
        });
      }

      const entry: TaskHistoryEntry = {
        id: uuidv4(),
        fieldPath: params.fieldPath,
        fieldName: params.fieldName,
        oldValue: params.oldValue,
        newValue: params.newValue,
        context: params.context,
        contextType: params.contextType,
        workflowStage: params.workflowStage,
        justification,
        userKey: activeUser.key,
        userName: activeUser.name,
        timestamp: new Date().toISOString(),
      };

      return storeChange(entry);
    } catch(error) {
      return false;
    }
  };

  return {
    // ===================================================================
    // State
    // ===================================================================
    justificationModal,
    
    // ===================================================================
    // Getters
    // ===================================================================
    getHistory,
    
    // ===================================================================
    // Setters
    // ===================================================================
    recordChange,
    
    // ===================================================================
    // Actions
    // ===================================================================
    requireJustification,
    submitJustification,
    cancelJustification,
  };
}

// ===================================================================
// Composable
// ===================================================================
// Singleton instance
export function useTaskHistory() {
  if (!instance) {
    instance = createTaskHistory();
  }
  return instance;
}